import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { FileText, Loader2, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Dialog dati di fatturazione: obbligatorio prima del checkout del piano a
 * pagamento. Tutti i campi sono required. Per PEC/SDI accettiamo "0000000"
 * come placeholder se l'utente non ha né PEC né codice SDI (specifica utente).
 */
export default function BillingInfoDialog({ open, onOpenChange, onSaved }) {
    const { user, setUser } = useAuth();
    const [form, setForm] = useState({
        billing_company_name: "",
        billing_vat_or_cf: "",
        billing_address: "",
        billing_email: "",
        billing_pec_or_sdi: "",
        billing_phone: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !user) return;
        setForm({
            billing_company_name: user.billing_company_name || user.restaurant_name || "",
            billing_vat_or_cf: user.billing_vat_or_cf || user.vat_number || "",
            billing_address:
                user.billing_address ||
                [user.restaurant_address, user.restaurant_city].filter(Boolean).join(", "),
            billing_email: user.billing_email || user.email || "",
            billing_pec_or_sdi: user.billing_pec_or_sdi || "",
            billing_phone: user.billing_phone || user.phone || "",
        });
    }, [open, user]);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e?.preventDefault?.();
        // Validazione minima client-side
        for (const [k, label] of [
            ["billing_company_name", "Ragione sociale / Nome e Cognome"],
            ["billing_vat_or_cf", "P.IVA / Codice Fiscale"],
            ["billing_address", "Indirizzo"],
            ["billing_email", "Email"],
            ["billing_pec_or_sdi", "PEC o codice SDI"],
            ["billing_phone", "Numero di telefono"],
        ]) {
            if (!form[k].trim()) {
                toast.error(`Compila il campo: ${label}`);
                return;
            }
        }
        setSaving(true);
        try {
            const { data } = await api.put("/auth/billing-info", form);
            setUser(data);
            toast.success("Dati di fatturazione salvati");
            onSaved?.(data);
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const fillPlaceholder = () => update("billing_pec_or_sdi", "0000000");

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl bg-white p-0 overflow-hidden max-h-[92vh] flex flex-col"
                data-testid="billing-info-dialog"
            >
                <DialogHeader className="px-6 pt-5 pb-4 border-b border-brand-line bg-gradient-to-r from-brand-gold/15 to-paper">
                    <div className="flex items-start gap-3">
                        <div className="h-11 w-11 rounded-full bg-brand-navy text-brand-gold flex items-center justify-center shrink-0">
                            <FileText size={20} />
                        </div>
                        <div className="min-w-0">
                            <DialogTitle className="font-display text-2xl text-brand-ink leading-tight">
                                Dati di fatturazione
                            </DialogTitle>
                            <DialogDescription className="text-sm text-brand-muted mt-1">
                                Inserisci i dati necessari per emettere fattura
                                elettronica prima di passare al piano a pagamento.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <form
                    onSubmit={submit}
                    className="flex-1 overflow-y-auto p-6 space-y-4"
                >
                    <Field
                        label="Ragione sociale / Nome e Cognome"
                        required
                    >
                        <input
                            value={form.billing_company_name}
                            onChange={(e) =>
                                update("billing_company_name", e.target.value)
                            }
                            placeholder="Es. Trattoria Da Mario di Mario Rossi"
                            data-testid="billing-company-name"
                            className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        />
                    </Field>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="P.IVA / Codice Fiscale" required>
                            <input
                                value={form.billing_vat_or_cf}
                                onChange={(e) =>
                                    update("billing_vat_or_cf", e.target.value)
                                }
                                placeholder="IT12345678901"
                                data-testid="billing-vat-or-cf"
                                className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm uppercase focus:outline-none focus:border-brand-gold"
                            />
                        </Field>
                        <Field label="Telefono" required>
                            <input
                                value={form.billing_phone}
                                onChange={(e) =>
                                    update("billing_phone", e.target.value)
                                }
                                placeholder="+39 06 1234567"
                                data-testid="billing-phone"
                                className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                            />
                        </Field>
                    </div>

                    <Field label="Indirizzo completo" required>
                        <input
                            value={form.billing_address}
                            onChange={(e) =>
                                update("billing_address", e.target.value)
                            }
                            placeholder="Via Roma 1, 00100 Roma (RM)"
                            data-testid="billing-address"
                            className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        />
                    </Field>

                    <Field label="Email per fatturazione" required>
                        <input
                            type="email"
                            value={form.billing_email}
                            onChange={(e) =>
                                update("billing_email", e.target.value)
                            }
                            placeholder="amministrazione@miorist.it"
                            data-testid="billing-email"
                            className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        />
                    </Field>

                    <Field
                        label="PEC oppure Codice destinatario SDI"
                        required
                        hint="Se non hai né PEC né SDI, inserisci 0000000"
                    >
                        <div className="flex gap-2">
                            <input
                                value={form.billing_pec_or_sdi}
                                onChange={(e) =>
                                    update(
                                        "billing_pec_or_sdi",
                                        e.target.value,
                                    )
                                }
                                placeholder="azienda@pec.it oppure 0000000"
                                data-testid="billing-pec-or-sdi"
                                className="flex-1 px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                            />
                            <button
                                type="button"
                                onClick={fillPlaceholder}
                                data-testid="billing-pec-fallback"
                                className="px-3 py-2.5 rounded-lg border border-brand-line text-[12px] font-semibold text-brand-muted hover:text-brand-navy hover:border-brand-gold/60 transition shrink-0"
                            >
                                Nessuna PEC/SDI
                            </button>
                        </div>
                    </Field>

                    <div className="flex items-start gap-2 p-3 rounded-lg bg-brand-cream/50 border border-brand-line">
                        <AlertTriangle
                            size={14}
                            className="text-brand-gold mt-0.5 shrink-0"
                        />
                        <div className="text-[11px] text-brand-muted leading-relaxed">
                            Questi dati saranno usati per emettere fattura
                            elettronica via SDI. Modificabili in qualsiasi momento
                            da questa stessa schermata.
                        </div>
                    </div>
                </form>

                <div className="px-6 py-4 bg-brand-cream border-t border-brand-line flex items-center justify-end gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={saving}
                        data-testid="billing-save"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition shadow disabled:opacity-60"
                    >
                        {saving ? (
                            <>
                                <Loader2 size={13} className="animate-spin" /> Salvo…
                            </>
                        ) : (
                            <>
                                <Check size={13} /> Salva e prosegui
                            </>
                        )}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, required, hint, children }) {
    return (
        <div>
            <div className="flex items-baseline gap-1 mb-1.5">
                <label className="eyebrow text-brand-ink/80">{label}</label>
                {required && <span className="text-brand-danger text-[10px]">*</span>}
                {hint && (
                    <span className="text-[10px] text-brand-muted ml-auto">
                        {hint}
                    </span>
                )}
            </div>
            {children}
        </div>
    );
}
